import { Tile } from '@components/shared/tile.component';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@components/ui/accordion';
import { Badge } from '@components/ui/badge';
import { AlertTriangleIcon } from 'lucide-react';
import type { FC } from 'react';
import type { BriefRiskItem } from './meia-p5-brief.util';

type BriefRisksTileProps = {
  fallbackMissionName?: string | null;
  fallbackThreadName?: string | null;
  risks: BriefRiskItem[];
};

export const BriefRisksTile: FC<BriefRisksTileProps> = (props) => {
  const { fallbackMissionName, fallbackThreadName, risks } = props;
  const groups = groupRisksByMission({ fallbackMissionName, risks });

  return (
    <Tile
      header={{
        title: (
          <span className="inline-flex items-center gap-2">
            <AlertTriangleIcon className="size-4 text-amber-600" />
            Open Risks
          </span>
        ),
      }}
    >
      {risks.length === 0 ? (
        <p className="text-xs text-gray-500">No open Brief risks detected for the selected context.</p>
      ) : (
        <Accordion
          className="flex flex-col gap-2"
          collapsible
          type="single"
        >
          {groups.map((group) => (
            <AccordionItem
              className="rounded-md border border-gray-200 bg-white"
              key={group.missionName}
              value={group.missionName}
            >
              <AccordionTrigger className="px-3 py-2 text-left hover:no-underline">
                <div className="flex min-w-0 items-center gap-2">
                  <Badge
                    size="sm"
                    variant={group.risks.some((risk) => risk.severity === 'H') ? 'destructive' : 'warning'}
                  >
                    {group.risks.length}
                  </Badge>
                  <span className="truncate text-sm font-medium text-gray-900">{group.missionName}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-3 pb-3">
                <div className="flex flex-col gap-2">
                  {group.risks.map((risk) => (
                    <RiskCard
                      fallbackThreadName={fallbackThreadName}
                      key={risk.id}
                      risk={risk}
                    />
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </Tile>
  );
};

type RiskGroup = {
  missionName: string;
  risks: BriefRiskItem[];
};

const groupRisksByMission = (params: {
  fallbackMissionName?: string | null;
  risks: BriefRiskItem[];
}): RiskGroup[] => {
  const { fallbackMissionName, risks } = params;
  const groups = new Map<string, BriefRiskItem[]>();

  for (const risk of risks) {
    const missionName = risk.missionName ?? fallbackMissionName ?? 'Study-level risks';
    const group = groups.get(missionName) ?? [];
    group.push(risk);
    groups.set(missionName, group);
  }

  return Array.from(groups.entries()).map(([missionName, groupRisks]) => ({
    missionName,
    risks: groupRisks,
  }));
};

type RiskCardProps = {
  fallbackThreadName?: string | null;
  risk: BriefRiskItem;
};

const RiskCard: FC<RiskCardProps> = (props) => {
  const { fallbackThreadName, risk } = props;
  const threadName = risk.threadName ?? fallbackThreadName;

  return (
    <div className="flex items-start gap-2 rounded-md border border-gray-200 px-3 py-2 text-xs">
      <Badge variant={risk.severity === 'H' ? 'destructive' : 'warning'}>{risk.severity}</Badge>
      <div className="min-w-0">
        <div className="font-medium text-gray-900">
          <RiskTitle
            risk={risk}
            threadName={threadName}
          />
        </div>
        <div className="mt-0.5 text-gray-500">{risk.mitigation}</div>
      </div>
    </div>
  );
};

type RiskTitleProps = {
  risk: BriefRiskItem;
  threadName?: string | null;
};

const RiskTitle: FC<RiskTitleProps> = (props) => {
  const { risk, threadName } = props;

  if (risk.stageName && threadName && risk.verdictBand) {
    return (
      <>
        <strong>{risk.stageName}</strong> stage of <strong>{threadName}</strong> has a {risk.verdictBand.toLowerCase()}{' '}
        verdict
      </>
    );
  }

  if (risk.stageName && threadName) {
    return (
      <>
        <strong>{risk.stageName}</strong> stage of <strong>{threadName}</strong> has no Tasks
      </>
    );
  }

  return <>{risk.title}</>;
};
